import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Menu, X } from "lucide-react";
import Logo, { LogoMark } from "./Logo";
import { scrollToHash } from "../lib/scroll";

const NAV = [
  { label: "Work", to: "/demos" },
  { label: "Process", to: "/#process" },
  { label: "About", to: "/#about" },
  { label: "FAQ", to: "/#faq" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const reduce = useReducedMotion();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.documentElement.style.overflow = open ? "hidden" : "";
    return () => {
      document.documentElement.style.overflow = "";
    };
  }, [open]);

  const handleHashLink = (e, to) => {
    e.preventDefault();
    setOpen(false);
    if (location.pathname !== "/") {
      navigate(to);
    } else {
      scrollToHash(to.slice(1));
    }
  };

  return (
    <header
      className={`fixed inset-x-0 top-0 z-40 transition-colors duration-300 ${
        open
          ? "border-b border-line bg-[#0B0D0F]"
          : scrolled
          ? "border-b border-line bg-elev/90 backdrop-blur-md"
          : "border-b border-transparent bg-transparent"
      }`}
      data-testid="site-header"
    >
      <div className="container-cr flex h-[72px] items-center justify-between">
        <Link
          to="/"
          className="rounded-md"
          aria-label="Clearriver Studios — home"
          data-testid="header-logo"
          onClick={() => setOpen(false)}
        >
          <Logo />
        </Link>

        <nav className="hidden items-center gap-8 md:flex" aria-label="Main">
          {NAV.map((item) =>
            item.to.startsWith("/#") ? (
              <a
                key={item.label}
                href={item.to}
                onClick={(e) => handleHashLink(e, item.to)}
                className="text-[14.5px] font-medium text-dim transition-colors hover:text-cream"
              >
                {item.label}
              </a>
            ) : (
              <Link
                key={item.label}
                to={item.to}
                className="text-[14.5px] font-medium text-dim transition-colors hover:text-cream"
              >
                {item.label}
              </Link>
            )
          )}
          <a
            href="/#contact"
            onClick={(e) => handleHashLink(e, "/#contact")}
            className="btn-primary h-10 px-5 text-sm"
            data-testid="header-cta"
          >
            Request a quote
          </a>
        </nav>

        <button
          type="button"
          className="flex h-11 w-11 cursor-pointer items-center justify-center rounded-[10px] border border-line text-cream md:hidden"
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
          data-testid="mobile-menu-toggle"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={reduce ? undefined : { opacity: 0 }}
            animate={reduce ? undefined : { opacity: 1 }}
            exit={reduce ? undefined : { opacity: 0 }}
            transition={{ duration: 0.25 }}
            style={{ backgroundColor: "#0B0D0F" }}
            className="fixed inset-0 top-[72px] z-30 flex flex-col md:hidden"
            data-testid="mobile-menu"
          >
            <nav className="container-cr flex flex-1 flex-col gap-2 pt-8" aria-label="Mobile">
              {NAV.map((item, i) => (
                <motion.div
                  key={item.label}
                  initial={reduce ? undefined : { opacity: 0, y: 14 }}
                  animate={reduce ? undefined : { opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.05 * i, ease: [0.22, 1, 0.36, 1] }}
                >
                  {item.to.startsWith("/#") ? (
                    <a
                      href={item.to}
                      onClick={(e) => handleHashLink(e, item.to)}
                      className="block border-b border-line py-5 font-serif text-3xl text-cream"
                    >
                      {item.label}
                    </a>
                  ) : (
                    <Link
                      to={item.to}
                      onClick={() => setOpen(false)}
                      className="block border-b border-line py-5 font-serif text-3xl text-cream"
                    >
                      {item.label}
                    </Link>
                  )}
                </motion.div>
              ))}
              <motion.div
                initial={reduce ? undefined : { opacity: 0, y: 14 }}
                animate={reduce ? undefined : { opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}
                className="pt-10"
              >
                <a
                  href="/#contact"
                  onClick={(e) => handleHashLink(e, "/#contact")}
                  className="btn-primary w-full"
                  data-testid="mobile-menu-cta"
                >
                  Request a quote
                </a>
                <div className="flex items-center justify-center gap-2 pt-8 text-xs text-dim">
                  <LogoMark size={16} />
                  Websites for landscaping &amp; hardscaping companies
                </div>
              </motion.div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
